import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { PlanarTopologyDatumBounds } from "../walls2/planarTopologyDatumBoundsTypes";
import {
  parsePlanarTopologyRelativeInset,
  serializePlanarTopologyRelativeInset,
} from "../walls2/planarTopologyRelativeInset";
import type { BuildingEntityType } from "./buildingEntityTypeTypes";

export interface BuildingEntityHandleBase<T extends BuildingEntityType> {
  readonly entityType: T;
  readonly id: string;
}

export type BuildingWallPlaneHandle = BuildingEntityHandleBase<"WALL_PLANE">;
export interface BuildingWallPlaneSubdomainHandle extends BuildingEntityHandleBase<"WALL_PLANE_SUBDOMAIN"> {
  readonly parent: BuildingWallPlaneHandle;
  readonly indexU: number;
  readonly indexV: number;
}
export interface BuildingGraphPlanarTopologyHandle extends BuildingEntityHandleBase<"BUILDING_GRAPH_PLANAR_TOPOLOGY"> {
  readonly startHorizontalDatumId?: ForeignEntityId<"DATUM">;
  readonly endHorizontalDatumId?: ForeignEntityId<"DATUM">;
  readonly startVerticalDatumId?: ForeignEntityId<"DATUM">;
  readonly endVerticalDatumId?: ForeignEntityId<"DATUM">;
  readonly relativeUStartInset?: number;
  readonly relativeUEndInset?: number;
  readonly relativeVBottomInset?: number;
  readonly relativeVTopInset?: number;
}
export type BuildingWallLayerHandle = BuildingEntityHandleBase<"WALL_LAYER">;
export type BuildingWallStructuralDomainHandle = BuildingEntityHandleBase<"WALL_STRUCTURAL_DOMAIN">;
type BuildingEntityHandleExcludingSpecial = {
  [K in Exclude<
    BuildingEntityType,
    "WALL_PLANE_SUBDOMAIN" | "BUILDING_GRAPH_PLANAR_TOPOLOGY"
  >]: BuildingEntityHandleBase<K>;
}[Exclude<BuildingEntityType, "WALL_PLANE_SUBDOMAIN" | "BUILDING_GRAPH_PLANAR_TOPOLOGY">];
export type BuildingEntityHandle =
  | BuildingEntityHandleExcludingSpecial
  | BuildingWallPlaneSubdomainHandle
  | BuildingGraphPlanarTopologyHandle;

export function isBuildingEntityHandleOfType<T extends BuildingEntityType>(
  handle: BuildingEntityHandle,
  entityType: T,
): handle is Extract<BuildingEntityHandle, { entityType: T }> {
  return handle.entityType === entityType;
}

export function isBuildingWallPlaneSubdomainHandle(
  handle: BuildingEntityHandle,
): handle is BuildingWallPlaneSubdomainHandle {
  return handle.entityType === "WALL_PLANE_SUBDOMAIN";
}

export function isBuildingGraphPlanarTopologyHandle(
  handle: BuildingEntityHandle,
): handle is BuildingGraphPlanarTopologyHandle {
  return handle.entityType === "BUILDING_GRAPH_PLANAR_TOPOLOGY";
}

export function readPlanarTopologyDatumBoundsFromHandle(
  handle: BuildingGraphPlanarTopologyHandle,
): PlanarTopologyDatumBounds {
  return {
    ...(handle.startHorizontalDatumId !== undefined ? { startHorizontalDatumId: handle.startHorizontalDatumId } : {}),
    ...(handle.endHorizontalDatumId !== undefined ? { endHorizontalDatumId: handle.endHorizontalDatumId } : {}),
    ...(handle.startVerticalDatumId !== undefined ? { startVerticalDatumId: handle.startVerticalDatumId } : {}),
    ...(handle.endVerticalDatumId !== undefined ? { endVerticalDatumId: handle.endVerticalDatumId } : {}),
  };
}

export function isBuildingEntityHandle(handle: any): handle is BuildingEntityHandle {
  return Boolean(handle && handle.entityType && handle.id);
}

const DELIMITER = ".";
const BOUNDS_START_MARKER = "@start:";
const BOUNDS_END_MARKER = "@end:";
const VERTICAL_BOUNDS_START_MARKER = "@vStart:";
const VERTICAL_BOUNDS_END_MARKER = "@vEnd:";
const RELATIVE_U_START_MARKER = "@ruStart:";
const RELATIVE_U_END_MARKER = "@ruEnd:";
const RELATIVE_V_BOTTOM_MARKER = "@rvBottom:";
const RELATIVE_V_TOP_MARKER = "@rvTop:";

function appendRelativeInsetHashSuffix(handle: BuildingGraphPlanarTopologyHandle, hash: string): string {
  if (
    handle.relativeUStartInset === undefined &&
    handle.relativeUEndInset === undefined &&
    handle.relativeVBottomInset === undefined &&
    handle.relativeVTopInset === undefined
  ) {
    return hash;
  }

  const ruStart = serializePlanarTopologyRelativeInset(handle.relativeUStartInset ?? 0);
  const ruEnd = serializePlanarTopologyRelativeInset(handle.relativeUEndInset ?? 0);
  const rvBottom = serializePlanarTopologyRelativeInset(handle.relativeVBottomInset ?? 0);
  const rvTop = serializePlanarTopologyRelativeInset(handle.relativeVTopInset ?? 0);

  return `${hash}${RELATIVE_U_START_MARKER}${ruStart}${RELATIVE_U_END_MARKER}${ruEnd}${RELATIVE_V_BOTTOM_MARKER}${rvBottom}${RELATIVE_V_TOP_MARKER}${rvTop}`;
}

function planarTopologyHandleHasBounds(handle: BuildingGraphPlanarTopologyHandle): boolean {
  return (
    handle.startHorizontalDatumId !== undefined ||
    handle.endHorizontalDatumId !== undefined ||
    handle.startVerticalDatumId !== undefined ||
    handle.endVerticalDatumId !== undefined ||
    handle.relativeUStartInset !== undefined ||
    handle.relativeUEndInset !== undefined ||
    handle.relativeVBottomInset !== undefined ||
    handle.relativeVTopInset !== undefined
  );
}

export function buildingEntityHandleHash(handle: BuildingEntityHandle): string {
  if (isBuildingGraphPlanarTopologyHandle(handle)) {
    if (!planarTopologyHandleHasBounds(handle)) {
      return `${handle.entityType}${DELIMITER}${handle.id}`;
    }

    const start = handle.startHorizontalDatumId ?? "";
    const end = handle.endHorizontalDatumId ?? "";
    const vStart = handle.startVerticalDatumId ?? "";
    const vEnd = handle.endVerticalDatumId ?? "";

    let hash = `${handle.entityType}${DELIMITER}${handle.id}`;
    if (start || end || vStart || vEnd) {
      hash += `${BOUNDS_START_MARKER}${start}${BOUNDS_END_MARKER}${end}`;
      if (vStart || vEnd) {
        hash += `${VERTICAL_BOUNDS_START_MARKER}${vStart}${VERTICAL_BOUNDS_END_MARKER}${vEnd}`;
      }
    }

    return appendRelativeInsetHashSuffix(handle, hash);
  }
  return `${handle.entityType}${DELIMITER}${handle.id}`;
}

function parseRelativeInsetsFromSuffix(suffix: string): {
  readonly remainder: string;
  readonly relativeInsets: Pick<
    BuildingGraphPlanarTopologyHandle,
    "relativeUStartInset" | "relativeUEndInset" | "relativeVBottomInset" | "relativeVTopInset"
  >;
} {
  const ruStartIdx = suffix.indexOf(RELATIVE_U_START_MARKER);
  if (ruStartIdx === -1) {
    return { remainder: suffix, relativeInsets: {} };
  }

  const ruEndIdx = suffix.indexOf(RELATIVE_U_END_MARKER, ruStartIdx);
  const rvBottomIdx = suffix.indexOf(RELATIVE_V_BOTTOM_MARKER, ruStartIdx);
  const rvTopIdx = suffix.indexOf(RELATIVE_V_TOP_MARKER, ruStartIdx);
  if (ruEndIdx === -1 || rvBottomIdx === -1 || rvTopIdx === -1) {
    return { remainder: suffix, relativeInsets: {} };
  }

  const relativeUStartInset = parsePlanarTopologyRelativeInset(
    suffix.slice(ruStartIdx + RELATIVE_U_START_MARKER.length, ruEndIdx),
  );
  const relativeUEndInset = parsePlanarTopologyRelativeInset(
    suffix.slice(ruEndIdx + RELATIVE_U_END_MARKER.length, rvBottomIdx),
  );
  const relativeVBottomInset = parsePlanarTopologyRelativeInset(
    suffix.slice(rvBottomIdx + RELATIVE_V_BOTTOM_MARKER.length, rvTopIdx),
  );
  const relativeVTopInset = parsePlanarTopologyRelativeInset(suffix.slice(rvTopIdx + RELATIVE_V_TOP_MARKER.length));

  return {
    remainder: suffix.slice(0, ruStartIdx),
    relativeInsets: {
      relativeUStartInset,
      relativeUEndInset,
      relativeVBottomInset,
      relativeVTopInset,
    },
  };
}

function parseDatumBoundsFromHashSuffix(handle: string): {
  readonly hashBody: string;
  readonly bounds: Pick<
    BuildingGraphPlanarTopologyHandle,
    | "startHorizontalDatumId"
    | "endHorizontalDatumId"
    | "startVerticalDatumId"
    | "endVerticalDatumId"
    | "relativeUStartInset"
    | "relativeUEndInset"
    | "relativeVBottomInset"
    | "relativeVTopInset"
  >;
} {
  const boundsStartIdx = handle.indexOf(BOUNDS_START_MARKER);
  if (boundsStartIdx === -1) {
    const relativeOnlyIdx = handle.indexOf(RELATIVE_U_START_MARKER);
    if (relativeOnlyIdx === -1) {
      return { hashBody: handle, bounds: {} };
    }
    const hashBody = handle.slice(0, relativeOnlyIdx);
    const { relativeInsets } = parseRelativeInsetsFromSuffix(handle.slice(relativeOnlyIdx));
    return { hashBody, bounds: relativeInsets };
  }

  const endMarkerIdx = handle.indexOf(BOUNDS_END_MARKER, boundsStartIdx);
  if (endMarkerIdx === -1) {
    return { hashBody: handle, bounds: {} };
  }

  const startValue = handle.slice(boundsStartIdx + BOUNDS_START_MARKER.length, endMarkerIdx);
  let suffix = handle.slice(endMarkerIdx + BOUNDS_END_MARKER.length);
  let endValue = suffix;
  let vStartValue = "";

  const verticalBoundsStartIdx = suffix.indexOf(VERTICAL_BOUNDS_START_MARKER);
  if (verticalBoundsStartIdx !== -1) {
    endValue = suffix.slice(0, verticalBoundsStartIdx);
    const verticalSuffix = suffix.slice(verticalBoundsStartIdx + VERTICAL_BOUNDS_START_MARKER.length);
    const verticalBoundsEndIdx = verticalSuffix.indexOf(VERTICAL_BOUNDS_END_MARKER);
    if (verticalBoundsEndIdx !== -1) {
      vStartValue = verticalSuffix.slice(0, verticalBoundsEndIdx);
      suffix = verticalSuffix.slice(verticalBoundsEndIdx + VERTICAL_BOUNDS_END_MARKER.length);
    }
  } else {
    suffix = suffix.slice(endValue.length);
  }

  const { relativeInsets, remainder: vEndValue } = parseRelativeInsetsFromSuffix(suffix);

  return {
    hashBody: handle.slice(0, boundsStartIdx),
    bounds: {
      ...(startValue ? { startHorizontalDatumId: startValue as ForeignEntityId<"DATUM"> } : {}),
      ...(endValue ? { endHorizontalDatumId: endValue as ForeignEntityId<"DATUM"> } : {}),
      ...(vStartValue ? { startVerticalDatumId: vStartValue as ForeignEntityId<"DATUM"> } : {}),
      ...(vEndValue ? { endVerticalDatumId: vEndValue as ForeignEntityId<"DATUM"> } : {}),
      ...relativeInsets,
    },
  };
}

export function parseBuildingEntityHandleHash(
  handle: string,
): BuildingEntityHandleBase<BuildingEntityType> | undefined {
  const { hashBody, bounds } = parseDatumBoundsFromHashSuffix(handle);
  const [entityType, ...idParts] = hashBody.split(DELIMITER);
  const id = idParts.join(DELIMITER);
  if (!entityType || !idParts.length || !id) {
    return undefined;
  }

  if (entityType === "BUILDING_GRAPH_PLANAR_TOPOLOGY") {
    return {
      entityType: "BUILDING_GRAPH_PLANAR_TOPOLOGY",
      id,
      ...bounds,
    };
  }

  return { entityType: entityType as BuildingEntityType, id };
}
